<template>
  <div class="video-upload">

    <!-- 单文件 -->
    <div v-if="limit === 1" class="single-mode">

      <!-- 上传 -->
      <el-upload v-if="canUpload" :action="uploadUrl" :headers="headers" :data="params" :show-file-list="false"
        :before-upload="beforeUpload" :on-success="uploadSuccess" :on-error="uploadError" accept="video/*">

        <div class="upload-box" v-loading="loading">
          <el-icon size="40">
            <VideoCameraFilled />
          </el-icon>

          <span>上传视频</span>
        </div>
      </el-upload>

      <!-- 预览 -->
      <div v-else class="preview-box">

        <video :src="videoUrl" controls preload="metadata" class="video" />

        <div class="mask">

          <el-upload :action="uploadUrl" :headers="headers" :data="params" :show-file-list="false"
            :before-upload="beforeUpload" :on-success="uploadSuccess" :on-error="uploadError" accept="video/*">

            <div class="replace-btn">
              点击更换
            </div>

          </el-upload>

        </div>

        <div class="delete-btn" @click="removeFile(0)">
          <el-icon>
            <Delete />
          </el-icon>
        </div>

      </div>

    </div>

    <!-- 多文件 -->
    <div v-else class="video-list">

      <div v-for="(item, index) in fileList" :key="item.uid || item.url" class="video-item">

        <video :src="item.url" controls preload="metadata" />

        <div class="video-name">
          {{ item.name }}
        </div>

        <div class="delete-btn" @click="removeFile(index)">
          <el-icon>
            <Delete />
          </el-icon>
        </div>

      </div>

      <!-- 上传按钮 -->
      <el-upload v-if="canUpload" :action="uploadUrl" :headers="headers" :data="params" :show-file-list="false"
        :before-upload="beforeUpload" :on-success="uploadSuccess" :on-error="uploadError" accept="video/*">

        <div class="upload-box" v-loading="loading">

          <el-icon size="40">
            <VideoCameraFilled />
          </el-icon>

          <span>继续上传</span>

        </div>

      </el-upload>

    </div>

    <!-- 提示 -->
    <div class="tip">

      请上传

      <span v-if="fileSize">
        小于
        <b>{{ fileSize }}MB</b>
      </span>

      <span v-if="fileType.length">
        格式为
        <b>{{ fileType.join('/') }}</b>
      </span>

      的视频文件

    </div>

  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
} from 'vue'

import {
  ElMessage,
} from 'element-plus'

import {
  VideoCameraFilled,
  Delete,
} from '@element-plus/icons-vue'

import {
  SYS_BASE_URL,
} from '@/constant/Const'

const props = defineProps({

  modelValue: {
    type: [String, Array],
    default: '',
  },

  limit: {
    type: Number,
    default: 1,
  },

  fileSize: {
    type: Number,
    default: 200,
  },

  fileType: {
    type: Array,
    default: () => [
      'mp4',
      'mov',
      'avi',
      'webm',
      'ogg',
    ],
  },

  width: {
    type: [String, Number],
    default: 240,
  },

  height: {
    type: [String, Number],
    default: 160,
  },

  params: {
    type: Object,
    default: () => ({}),
  },

})

const emit = defineEmits([
  'update:modelValue',
  'success',
  'remove',
])

const loading = ref(false)

const fileList = ref([])

const uploadUrl =
  SYS_BASE_URL + '/api/img/uploadSign'

const headers = {
  Authorization:
    sessionStorage.getItem('token'),
}

const canUpload = computed(() => {
  return fileList.value.length < props.limit
})

const videoUrl = computed(() => {
  return fileList.value?.[0]?.url || ''
})

const getFileName = (url = '') => {
  return url.substring(
    url.lastIndexOf('/') + 1
  )
}

watch(
  () => props.modelValue,
  (val) => {

    const arr = String(val || '')
      .split(',')
      .filter(Boolean)

    const newList = arr.map((url, index) => ({
      name: getFileName(url),
      url,
      uid: `${index}_${url}`,
    }))

    if (
      JSON.stringify(newList) !==
      JSON.stringify(fileList.value)
    ) {
      fileList.value = newList
    }

  },
  {
    immediate: true,
  }
)

const beforeUpload = (file) => {

  loading.value = true

  const ext =
    file.name
      .split('.')
      .pop()
      ?.toLowerCase() || ''

  if (
    props.fileType.length &&
    !props.fileType.includes(ext)
  ) {

    loading.value = false

    ElMessage.error(
      `请上传 ${props.fileType.join('/')} 格式视频`
    )

    return false
  }

  const isLt =
    file.size / 1024 / 1024 <
    props.fileSize

  if (!isLt) {

    loading.value = false

    ElMessage.error(
      `视频大小不能超过 ${props.fileSize}MB`
    )

    return false
  }

  return true
}

const uploadSuccess = (res) => {

  loading.value = false

  if (res.code !== 1) {
    ElMessage.error(
      res.msg || '上传失败'
    )
    return
  }

  const fileInfo = res.data

  const url = `https://${fileInfo.domain}/${fileInfo.pathPrefix}${fileInfo.storageFilePath}${fileInfo.newFileName}`;

  const item = {
    name: getFileName(url),
    url,
    uid: Date.now(),
  }

  if (props.limit === 1) {
    fileList.value = [item]
  } else {
    fileList.value.push(item)
  }

  emitValue()

  emit('success', url)
}

const uploadError = () => {

  loading.value = false

  ElMessage.error('上传失败')
}

const removeFile = (index) => {

  const item =
    fileList.value[index]

  fileList.value.splice(index, 1)

  emitValue()

  emit('remove', item)
}

const emitValue = () => {
  const urls =
    fileList.value.map(
      item => item.url
    )

  emit(
    'update:modelValue',
    urls.join(',')
  )
}


</script>

<style lang="scss" scoped>
.video-upload {
  width: 100%;
}

.single-mode {
  width: fit-content;
}

.upload-box {
  width: v-bind('props.width + "px"');
  height: v-bind('props.height + "px"');

  border: 1px dashed #dcdfe6;
  border-radius: 14px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: 10px;

  color: #909399;

  cursor: pointer;

  transition: all .3s;

  background: #fafafa;

  overflow: hidden;

  &:hover {
    border-color: #409eff;
    color: #409eff;
    background: #f0f9ff;
  }
}

.preview-box {
  position: relative;

  .video {
    width: v-bind('props.width + "px"');
    height: v-bind('props.height + "px"');

    object-fit: cover;

    border-radius: 14px;

    background: #000;
  }

  .mask {
    position: absolute;
    inset: 0;

    display: flex;
    align-items: center;
    justify-content: center;

    background: rgba(0, 0, 0, .45);

    opacity: 0;

    transition: .3s;

    border-radius: 14px;
  }

  &:hover .mask {
    opacity: 1;
  }

  .replace-btn {
    color: #fff;
    font-size: 15px;
    cursor: pointer;
  }
}

.video-list {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.video-item {
  width: v-bind('props.width + "px"');
  position: relative;

  video {
    width: 100%;
    height: v-bind('props.height + "px"');

    object-fit: cover;

    border-radius: 14px;

    background: #000;
  }

  .video-name {
    margin-top: 8px;

    font-size: 13px;
    color: #606266;

    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.delete-btn {
  position: absolute;
  top: 10px;
  right: 10px;

  width: 30px;
  height: 30px;

  border-radius: 50%;

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(0, 0, 0, .55);

  color: #fff;

  cursor: pointer;

  transition: .2s;

  z-index: 2;

  &:hover {
    transform: scale(1.08);
    background: rgba(255, 0, 0, .85);
  }
}

.tip {
  margin-top: 12px;

  font-size: 13px;
  color: #909399;

  line-height: 1.6;

  b {
    color: #f56c6c;
    margin: 0 2px;
  }
}
</style>